import React from 'react'
import { ExternalLinkIcon, MapPinIcon } from 'lucide-react'
import {
  formatAccuracyM,
  formatCoordinates,
  googleMapsUrl,
} from '../../lib/geoDisplay'

export type JobFieldCaptureFields = {
  actual_start_time?: string | null
  actual_end_time?: string | null
  start_latitude?: number | null
  start_longitude?: number | null
  start_accuracy_m?: number | null
  complete_latitude?: number | null
  complete_longitude?: number | null
  complete_accuracy_m?: number | null
}

function fmtTime(iso?: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString()
}

function LocationBlock({
  label,
  at,
  lat,
  lng,
  accuracy,
}: {
  label: string
  at?: string | null
  lat?: number | null
  lng?: number | null
  accuracy?: number | null
}) {
  const maps = googleMapsUrl(lat, lng)
  const hasGps = lat != null && lng != null
  if (!at && !hasGps) return null

  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50 p-3 space-y-1.5">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
      <p className="text-sm text-gray-900">{fmtTime(at)}</p>
      <p className="text-sm text-gray-700 inline-flex items-start gap-1.5">
        <MapPinIcon className="w-3.5 h-3.5 text-gray-400 mt-0.5 shrink-0" />
        <span>
          {formatCoordinates(lat, lng)}
          {hasGps && (
            <span className="text-xs text-gray-500"> · {formatAccuracyM(accuracy)}</span>
          )}
        </span>
      </p>
      {maps && (
        <a
          href={maps}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline"
        >
          Open in Maps <ExternalLinkIcon className="w-3 h-3" />
        </a>
      )}
      {!hasGps && at && (
        <p className="text-xs text-amber-700">No GPS captured for this event</p>
      )}
    </div>
  )
}

/** Admin-facing field start / complete times + GPS from the job record. */
export function JobFieldCapturePanel({ job }: { job: JobFieldCaptureFields }) {
  const hasStart =
    !!job.actual_start_time ||
    (job.start_latitude != null && job.start_longitude != null)
  const hasComplete =
    !!job.actual_end_time ||
    (job.complete_latitude != null && job.complete_longitude != null)

  if (!hasStart && !hasComplete) return null

  return (
    <div className="rounded-lg border border-gray-200 p-4">
      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <MapPinIcon className="w-4 h-4" /> Field capture
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <LocationBlock
          label="Job start"
          at={job.actual_start_time}
          lat={job.start_latitude}
          lng={job.start_longitude}
          accuracy={job.start_accuracy_m}
        />
        <LocationBlock
          label="Job complete"
          at={job.actual_end_time}
          lat={job.complete_latitude}
          lng={job.complete_longitude}
          accuracy={job.complete_accuracy_m}
        />
      </div>
    </div>
  )
}
